/**
 * Creative Intelligence workflow — pure stage model + gating (Task 235).
 *
 * The page renders ONE stage at a time (a switcher driven by the stepper). This
 * module is the single source of truth for the stage order and for which stages
 * are available / locked / coming-soon, given the current workflow signals. It
 * is pure (no React, no I/O, no AI) so it can be unit-tested directly — same
 * pattern as the Task 230 `creativeAnalysisGate.ts`.
 *
 * Gating rules (the workflow's load-bearing UX contract):
 *   - performance: always available.
 *   - upload:      "coming_soon" until standalone CI bulk upload has backend
 *                  support (see CREATIVE_BULK_UPLOAD_ENABLED + tasks/235).
 *   - match:       available (works against existing assets via Task 222).
 *   - analysis:    LOCKED until at least one creative asset is matched OR
 *                  real analyses already exist (Task 260 — results are never
 *                  hidden behind a prerequisite that was met historically).
 *   - strategy:    LOCKED until creative analysis is ready (Task 224/225).
 */

export type CreativeWorkflowStageId = 'performance' | 'upload' | 'match' | 'analysis' | 'strategy';

export interface CreativeWorkflowStageDef {
  readonly id: CreativeWorkflowStageId;
  /** 1-based display number. */
  readonly n: number;
  /** Arabic stepper label. */
  readonly label: string;
}

/**
 * The ordered stages. Order is load-bearing: performance → upload → match →
 * analysis → strategy. The shape test pins this order against the page source.
 */
export const CREATIVE_WORKFLOW_STAGES: ReadonlyArray<CreativeWorkflowStageDef> = [
  { id: 'performance', n: 1, label: 'الأداء عبر المنصات' },
  { id: 'upload', n: 2, label: 'رفع الإبداعات' },
  { id: 'match', n: 3, label: 'مطابقة الأصول' },
  { id: 'analysis', n: 4, label: 'تحليل الإبداع' },
  { id: 'strategy', n: 5, label: 'اختيار الاستراتيجية' },
];

/**
 * Whether standalone Creative-Intelligence BULK upload is wired to a backend.
 * `true` since Task 237: the Task 236 backend is live (create-ci-asset-uploads /
 * complete-ci-asset-uploads EFs + the creative_intelligence_upload_intents table
 * + RPC), and the Upload stage calls it via the FE create → PUT(signed) →
 * complete flow. When true the stepper shows the Upload stage as available.
 */
export const CREATIVE_BULK_UPLOAD_ENABLED = true;

export interface CreativeWorkflowGateInputs {
  /** At least one creative asset is linked to its ads (group match_quality === 'asset_match'). */
  readonly hasMatchedAssets: boolean;
  /** Creative analysis has produced results — REAL since Task 260 (the page
   *  derives it from the get-creative-analysis-status read). */
  readonly analysisReady: boolean;
  /** Standalone bulk upload is backed by an endpoint (see CREATIVE_BULK_UPLOAD_ENABLED). */
  readonly uploadEnabled: boolean;
}

export type CreativeWorkflowStageState = 'available' | 'locked' | 'coming_soon';

/** Resolve a stage's state from the current workflow signals. */
export function creativeWorkflowStageState(
  id: CreativeWorkflowStageId,
  gate: CreativeWorkflowGateInputs,
): CreativeWorkflowStageState {
  switch (id) {
    case 'performance':
      return 'available';
    case 'upload':
      return gate.uploadEnabled ? 'available' : 'coming_soon';
    case 'match':
      return 'available';
    case 'analysis':
      // Task 260: results already existing prove the prerequisite was met
      // historically — never lock a stage that has real output to show.
      return gate.hasMatchedAssets || gate.analysisReady ? 'available' : 'locked';
    case 'strategy':
      return gate.analysisReady ? 'available' : 'locked';
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

/** Convenience: is the stage hard-locked (a prerequisite is missing)? */
export function isCreativeWorkflowStageLocked(
  id: CreativeWorkflowStageId,
  gate: CreativeWorkflowGateInputs,
): boolean {
  return creativeWorkflowStageState(id, gate) === 'locked';
}

/**
 * Normalize a creative filename to the token used for matching against ad /
 * creative names. Strips any path + extension, lowercases, collapses
 * `._-` separators and whitespace to single spaces, and trims. Pure +
 * deterministic. Used by the (disabled) Upload stage to preview the match name.
 */
export function normalizeCreativeFilename(filename: string): string {
  const base = filename
    .replace(/^.*[\\/]/, '') // drop any directory prefix
    .replace(/\.[a-z0-9]+$/i, ''); // drop a trailing extension
  return base
    .toLowerCase()
    .replace(/[._\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
